import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {Box, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Topbar from './Topbar';

const BUILD_VERSION = process.env.REACT_APP_VERSION ?? 'dev';
const BUILD_DATE = process.env.REACT_APP_BUILD_DATE ?? '';

type AnalysisCard = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  route: string;
  available: boolean;
  icon: string;
  stat?: string;
  statLabel?: string;
};

const CARDS: AnalysisCard[] = [
  {
    id: 'messages',
    title: 'Fehlende Meldungen',
    subtitle: 'Missing Messages',
    description: 'Identify gaps in forecast message coverage across routes and stations. Detect which departures are failing to generate occupancy predictions.',
    route: '/analysis/messages',
    available: true,
    icon: '⚠',
    stat: '—',
    statLabel: 'gaps detected',
  },
  {
    id: 'coverage',
    title: 'Streckenabdeckung',
    subtitle: 'Route Coverage',
    description: 'Visualise which routes and stop sequences have consistent message coverage versus sporadic or absent reporting.',
    route: '/analysis/coverage',
    available: true,
    icon: '◈',
    stat: '—',
    statLabel: 'routes monitored',
  },
  {
    id: 'delays',
    title: 'Verspätungsmuster',
    subtitle: 'Delay Patterns',
    description: 'Explore delay distributions across lines, times of day, and station sequences. Surface systemic patterns in forecast accuracy.',
    route: '/analysis/delays',
    available: false,
    icon: '◷',
    stat: '—',
    statLabel: 'avg delay (min)',
  },
  {
    id: 'occupancy',
    title: 'Auslastungstrends',
    subtitle: 'Occupancy Trends',
    description: 'Analyse passenger load trends over time by line and carriage group. Compare live observations against forecast prognoses.',
    route: '/analysis/occupancy',
    available: false,
    icon: '▣',
    stat: '—',
    statLabel: 'avg occupancy',
  },
];

const AnalysisLanding: React.FC = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Stagger mount for animation
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#0d1b2a',
      fontFamily: '"IBM Plex Sans", "Helvetica Neue", sans-serif',
      backgroundImage: `
        linear-gradient(rgba(240,165,0,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(240,165,0,0.03) 1px, transparent 1px)
      `,
      backgroundSize: '48px 48px',
    }}>

      {/* Import IBM Plex Sans */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      <Topbar menuState={false} openAction={() => {}} />

      {/* Main content */}
      <Box sx={{ pt: '64px', px: { xs: 3, md: 8 }, pb: 8 }}>

        {/* Header */}
        <Box sx={{
          pt: 8, pb: 6,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}>
          <Typography sx={{
            fontFamily: '"IBM Plex Mono"',
            fontSize: '11px',
            letterSpacing: '0.2em',
            color: '#f0a500',
            textTransform: 'uppercase',
            mb: 2,
          }}>
            Stuttgart S-Bahn · Datenanalyse
          </Typography>
          <Typography sx={{
            fontFamily: '"IBM Plex Sans"',
            fontSize: { xs: '28px', md: '40px' },
            fontWeight: 300,
            color: '#ffffff',
            lineHeight: 1.15,
            mb: 2,
          }}>
            Analyse &{' '}
            <span style={{ color: '#f0a500', fontWeight: 500 }}>Qualitätssicherung</span>
          </Typography>
          <Typography sx={{
            fontFamily: '"IBM Plex Sans"',
            fontSize: '15px',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.45)',
            maxWidth: '540px',
            lineHeight: 1.7,
          }}>
            Diagnostic tools for exploring forecast message coverage, delay patterns,
            and occupancy data quality across the Stuttgart S-Bahn network.
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{
          height: '1px',
          background: 'linear-gradient(90deg, #f0a500 0%, rgba(240,165,0,0.1) 40%, transparent 100%)',
          mb: 6,
          opacity: mounted ? 1 : 0,
          transition: 'opacity 0.5s ease 0.2s',
        }} />

        {/* Cards grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}>
          {CARDS.map((card, i) => (
            <Box
              key={card.id}
              onClick={() => card.available && navigate(card.route)}
              sx={{
                position: 'relative',
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '6px',
                padding: '32px',
                cursor: card.available ? 'pointer' : 'default',
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.5s ease ${0.15 + i * 0.08}s, transform 0.5s ease ${0.15 + i * 0.08}s, border-color 0.2s ease, background-color 0.2s ease`,
                '&:hover': card.available ? {
                  borderColor: 'rgba(240,165,0,0.5)',
                  backgroundColor: 'rgba(240,165,0,0.04)',
                } : {},
                overflow: 'hidden',
              }}
            >
              {/* Coming soon overlay */}
              {!card.available && (
                <Box sx={{
                  position: 'absolute',
                  top: 16, right: 16,
                  backgroundColor: 'rgba(240,165,0,0.1)',
                  border: '1px solid rgba(240,165,0,0.25)',
                  borderRadius: '3px',
                  padding: '2px 8px',
                }}>
                  <Typography sx={{ fontSize: '10px', color: '#f0a500', fontFamily: '"IBM Plex Mono"', letterSpacing: '0.1em' }}>
                    IN VORBEREITUNG
                  </Typography>
                </Box>
              )}

              {/* Icon */}
              <Typography sx={{
                fontSize: '28px',
                color: 'rgba(240,165,0,0.6)',
                mb: 2,
                fontFamily: '"IBM Plex Mono"',
                lineHeight: 1,
              }}>
                {card.icon}
              </Typography>

              {/* Title */}
              <Typography sx={{
                fontFamily: '"IBM Plex Sans"',
                fontSize: '18px',
                fontWeight: 500,
                color: '#ffffff',
                mb: 0.5,
              }}>
                {card.title}
              </Typography>
              <Typography sx={{
                fontFamily: '"IBM Plex Mono"',
                fontSize: '11px',
                color: 'rgba(240,165,0,0.7)',
                letterSpacing: '0.1em',
                mb: 2,
              }}>
                {card.subtitle}
              </Typography>

              {/* Description */}
              <Typography sx={{
                fontFamily: '"IBM Plex Sans"',
                fontSize: '13px',
                fontWeight: 300,
                color: 'rgba(255,255,255,0.45)',
                lineHeight: 1.7,
                mb: 3,
              }}>
                {card.description}
              </Typography>

              {/* Stat */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{
                  fontFamily: '"IBM Plex Mono"',
                  fontSize: '24px',
                  fontWeight: 500,
                  color: card.available ? '#f0a500' : 'rgba(255,255,255,0.15)',
                }}>
                  {card.stat}
                </Typography>
                <Typography sx={{
                  fontFamily: '"IBM Plex Sans"',
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.25)',
                }}>
                  {card.statLabel}
                </Typography>
              </Box>

              {/* Bottom accent line */}
              <Box sx={{
                position: 'absolute',
                bottom: 0, left: 0, right: 0,
                height: '2px',
                background: card.available
                  ? 'linear-gradient(90deg, #f0a500, transparent)'
                  : 'linear-gradient(90deg, rgba(255,255,255,0.05), transparent)',
              }} />
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{
          mt: 8, pt: 4,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 0.5s ease 0.6s',
        }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>
            STUTTGART S-BAHN · OCCUPANCY INTELLIGENCE
          </Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>
            {BUILD_VERSION}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AnalysisLanding;